import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

interface PortalFlags {
  isEditor: boolean;
  isProducer: boolean;
  isAdmin: boolean;
}

interface UseMyPortalsResult extends PortalFlags {
  isLoading: boolean;
}

/**
 * Robust portal detection via SECURITY DEFINER RPC.
 * Bypasses RLS issues with direct table queries.
 */
export function useMyPortals(): UseMyPortalsResult {
  const { user, isLoading: authLoading } = useAuth();

  const { data, isLoading: queryLoading } = useQuery({
    queryKey: ['my-portals', user?.id],
    queryFn: async (): Promise<PortalFlags> => {
      const { data, error } = await supabase.rpc('get_my_portals');
      if (error) {
        console.error('get_my_portals error:', error);
        return { isEditor: false, isProducer: false, isAdmin: false };
      }
      const result = data as unknown as { is_editor: boolean; is_producer: boolean; is_admin: boolean };
      return {
        isEditor: result?.is_editor ?? false,
        isProducer: result?.is_producer ?? false,
        isAdmin: result?.is_admin ?? false,
      };
    },
    enabled: !!user && !authLoading,
    staleTime: 1000 * 60 * 5,
  });

  return {
    isEditor: data?.isEditor ?? false,
    isProducer: data?.isProducer ?? false,
    isAdmin: data?.isAdmin ?? false,
    isLoading: authLoading || queryLoading,
  };
}
